import { BaseModal } from "@classdojo/web/components/modals";
import { BodyText, Button, HeadlineText, LabelText } from "@classdojo/web/nessie";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { IconChevronLeft } from "@web-monorepo/dds-icons";
import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import React from "react";
import logo from "src/pods/groups/assets/group_creator.png";
import { GroupRestriction } from "src/pods/groups/createGroups";
import { Student } from "src/pods/students";
import OldAppModal from "src/shared/components/OldAppModal";
import SelectStudents from "src/shared/components/SelectStudents";

type GroupRestrictionsProps = {
  classId: string;
  editGroupRestrictions: ({
    classId,
    groupOfStudentIdGroups,
  }: {
    classId: string;
    groupOfStudentIdGroups: GroupRestriction[];
  }) => void;
  onRequestHide: () => void;
  students: Student[];
  groupRestrictions: GroupRestriction[];
};

type GroupRestrictionsState = {
  askForRestriction: boolean;
  newRestriction: GroupRestriction | null;
};

const initialState = {
  askForRestriction: false,
  newRestriction: null,
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class GroupRestrictions extends React.Component<GroupRestrictionsProps, GroupRestrictionsState> {
  constructor(props: GroupRestrictionsProps) {
    super(props);
    this.state = initialState;
  }

  render() {
    const { students, groupRestrictions } = this.props;
    const { askForRestriction } = this.state;

    return (
      <OldAppModal>
        <article sx={appStyle}>
          <header sx={headerStyle}>
            <Button
              icon={<IconChevronLeft size="m" />}
              sx={backButtonStyle}
              kind="tertiary"
              onClick={this.props.onRequestHide}
              data-name="groupRestrictionsClose"
              aria-label={autoTranslate("Go back")}
            />
            <HeadlineText level={2} as="h1">
              Don't group together
            </HeadlineText>
          </header>
          <section sx={contentStyle}>
            <img src={logo} sx={logoStyle} role="presentation" />
            <BodyText sx={descriptionStyle}>Create sets of students who you don't want grouped together.</BodyText>
            <button
              data-name="group_restrictions:render:group_maker_restrictions_choose_students"
              sx={buttonStyle}
              onClick={this.openAddRestriction}
            >
              <LabelText kind="accent">Choose students</LabelText>
            </button>
            <div>{groupRestrictions && groupRestrictions.map(this._renderRestriction)}</div>
          </section>
        </article>
        {askForRestriction && (
          <BaseModal data-name="group_restrictions:render:base_modal" sx={selectStudentsModalStyle} label="">
            <SelectStudents
              students={students}
              onChange={this.updateNewRestriction}
              onRequestHide={this.closeAddRestriction}
            />
          </BaseModal>
        )}
      </OldAppModal>
    );
  }

  _renderStudent = (studentId: string) => {
    const student = this.props.students.find((student) => student._id === studentId);
    if (!student) return null;
    const lastLetter = student.lastName.slice(0, 1);

    return (
      <div key={student._id} sx={studentStyle}>
        <img sx={avatarStyle} src={student.avatar} role="presentation" />
        <BodyText level={2} sx={nameStyle}>
          {student.firstName} {lastLetter}.
        </BodyText>
      </div>
    );
  };

  _renderRestriction = (studentIds: GroupRestriction) => {
    const restrictions = studentIds.map(this._renderStudent).filter(Boolean);
    if (restrictions.length > 0) {
      return (
        <section sx={restrictionStyle}>
          <button
            data-name="group_restrictions:clear_student_selection"
            sx={deleteButtonStyle}
            onClick={() => this.deleteRestriction(studentIds)}
            aria-label={autoTranslate("Clear student selection")}
            disable-tag-translation
          >
            {"×"}
          </button>
          {restrictions}
        </section>
      );
    } else {
      return null;
    }
  };

  openAddRestriction = () => {
    this.setState({ askForRestriction: true, newRestriction: [] });
  };

  closeAddRestriction = () => {
    const { classId, groupRestrictions } = this.props;
    const { newRestriction } = this.state;
    const newGroupRestrictions = newRestriction?.length
      ? [...(groupRestrictions || []), newRestriction]
      : groupRestrictions;
    this.setState(
      {
        askForRestriction: false,
        newRestriction: null,
      },
      () =>
        this.props.editGroupRestrictions({
          classId,
          groupOfStudentIdGroups: newGroupRestrictions,
        }),
    );
  };

  updateNewRestriction = (selectedStudents: Student[]) => {
    this.setState({ newRestriction: selectedStudents.map((student) => student._id) });
  };

  deleteRestriction = (restriction: GroupRestriction) => {
    const { classId, groupRestrictions } = this.props;
    this.props.editGroupRestrictions({
      classId,
      groupOfStudentIdGroups: groupRestrictions.filter((r) => r !== restriction),
    });
  };
}

const appStyle: ThemeUIStyleObject = {
  textAlign: "center",
};

const headerStyle: ThemeUIStyleObject = {
  position: "relative",
  paddingY: "dt_l",
  borderBottom: "dt_divider",
};

const backButtonStyle: ThemeUIStyleObject = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  left: "10px",
};

const contentStyle: ThemeUIStyleObject = {
  margin: RAW_cssValue("0 24px"),
};

const logoStyle: ThemeUIStyleObject = {
  margin: RAW_cssValue("32px auto 23px auto"),
};

const descriptionStyle: ThemeUIStyleObject = {
  marginBottom: "dt_xl",
};

const cellStyle: ThemeUIStyleObject = {
  display: "block",
  width: "100%",
  marginY: "dt_m",
  border: "dt_card",
  borderRadius: "dt_radius_s",
  boxShadow: "dt_shadow_shadezies",
  backgroundColor: "transparent",
};

const buttonStyle: ThemeUIStyleObject = {
  ...cellStyle,
  height: "64px",
};

const restrictionStyle: ThemeUIStyleObject = {
  ...cellStyle,
  position: "relative",
};

const deleteButtonStyle: ThemeUIStyleObject = {
  cursor: "pointer",
  position: "absolute",
  top: "0",
  right: "0",
  padding: RAW_cssValue("5px 10px"),
  fontSize: "28px",
  fontWeight: "600",
  color: "dt_content_primary",
  border: "none",
  backgroundColor: "transparent",
};

const studentStyle: ThemeUIStyleObject = {
  margin: RAW_cssValue("15px 20px"),
  textAlign: "left",
  verticalAlign: "middle",
  lineHeight: "20px",
};

const nameStyle: ThemeUIStyleObject = {
  display: "inline-block",
  marginRight: "auto",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  verticalAlign: "middle",
};

const avatarStyle: ThemeUIStyleObject = {
  display: "inline-block",
  width: "17px",
  height: "20px",
  margin: RAW_cssValue("0 13px 0 0"),
  verticalAlign: "middle",
};

const selectStudentsModalStyle: ThemeUIStyleObject = {
  maxWidth: "100%",
  width: "100%",
  height: "100%",
  margin: "0",
  border: "none",
  borderRadius: "0",
};
